import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import type { SearchResultItem } from "@atlas/contracts";
import { ApiError, fetchSearch } from "../lib/api";
import { useDebouncedValue } from "../lib/useDebouncedValue";
import { DOMAIN_LABELS, SEARCH_TYPE_LABELS } from "../lib/routes";
import { Icon } from "./Icon";
import { LoadingState, ErrorState, EmptyState } from "./StateViews";

const MIN_QUERY_LENGTH = 2;

const QUICK_LINKS: Array<{ label: string; path: string }> = [
  { label: DOMAIN_LABELS.books, path: "/books" },
  { label: DOMAIN_LABELS.media, path: "/media" },
  { label: DOMAIN_LABELS.places, path: "/places" },
  { label: DOMAIN_LABELS.finance, path: "/more/finance" },
  { label: DOMAIN_LABELS.projects, path: "/more/projects" },
  { label: DOMAIN_LABELS.shopping, path: "/more/shopping" },
];

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

/** Full-screen search overlay (v2 pattern) — real API-backed search with loading/error/empty/results states. */
export function GlobalSearch({ open, onClose, onNavigate }: SearchOverlayProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResultItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [reloadToken, setReloadToken] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmedQuery = query.trim();
  const debouncedQuery = useDebouncedValue(trimmedQuery, 300);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || debouncedQuery.length < MIN_QUERY_LENGTH) {
      setResults(null);
      setError(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetchSearch(debouncedQuery, controller.signal)
      .then((response) => {
        setResults(response.results);
        setActiveIndex(-1);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setResults(null);
        setError(err instanceof ApiError ? err.message : "جست‌وجو با خطا مواجه شد.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, debouncedQuery, reloadToken]);

  if (!open) return null;

  function clear() {
    setQuery("");
    setResults(null);
    setError(null);
    setActiveIndex(-1);
  }

  function select(item: SearchResultItem) {
    clear();
    onClose();
    onNavigate(item.href);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (!results || results.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => (current <= 0 ? results.length - 1 : current - 1));
    } else if (event.key === "Enter") {
      const item = activeIndex >= 0 ? results[activeIndex] : results[0];
      if (item) {
        event.preventDefault();
        select(item);
      }
    }
  }

  const showMinHint = trimmedQuery.length > 0 && trimmedQuery.length < MIN_QUERY_LENGTH;
  const showIdle = trimmedQuery.length === 0;

  return (
    <div className="search-overlay" role="dialog" aria-modal="true" aria-label="جست‌وجوی سراسری">
      <div className="search-overlay__header">
        <label className="search-field search-overlay__field">
          <Icon name="search" width={15} height={15} aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            role="combobox"
            aria-expanded={results !== null && results.length > 0}
            aria-autocomplete="list"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="در اطلس جست‌وجو کن…"
          />
          {query && (
            <button type="button" onClick={clear} aria-label="پاک کردن جست‌وجو" style={{ background: "none", border: "none", color: "inherit", cursor: "pointer" }}>
              <Icon name="x" width={13} height={13} aria-hidden="true" />
            </button>
          )}
        </label>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="بستن جست‌وجو">
          <Icon name="x" width={14} height={14} aria-hidden="true" />
        </button>
      </div>

      <div className="search-overlay__body">
        {showIdle && (
          <>
            <p className="filter-summary">دسترسی سریع</p>
            <div className="quick-links">
              {QUICK_LINKS.map((link) => (
                <button
                  key={link.path}
                  type="button"
                  className="quick-link-row"
                  onClick={() => {
                    onClose();
                    onNavigate(link.path);
                  }}
                >
                  {link.label}
                </button>
              ))}
            </div>
          </>
        )}
        {!showIdle && showMinHint && <p className="filter-summary">حداقل ۲ نویسه وارد کنید.</p>}
        {!showIdle && !showMinHint && loading && <LoadingState label="در حال جست‌وجو…" />}
        {!showIdle && !showMinHint && !loading && error && (
          <ErrorState message={error} onRetry={() => setReloadToken((t) => t + 1)} />
        )}
        {!showIdle && !showMinHint && !loading && !error && results && results.length === 0 && (
          <EmptyState message={`نتیجه‌ای برای «${debouncedQuery}» یافت نشد.`} />
        )}
        {!showIdle && !showMinHint && !loading && !error && results && results.length > 0 && (
          <div>
            {results.map((item, index) => (
              <button
                key={`${item.type}-${item.id}`}
                type="button"
                className="search-result-row"
                onClick={() => select(item)}
                onMouseEnter={() => setActiveIndex(index)}
                data-active={index === activeIndex || undefined}
              >
                <p className="search-result-row__title">{item.title}</p>
                <div className="search-result-row__meta">
                  <span className="search-result-row__type">{SEARCH_TYPE_LABELS[item.type]}</span>
                  {item.subtitle && <span className="search-result-row__subtitle">{item.subtitle}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
